import {actions, useApp} from "../providers/AppProvider.tsx";
import {useMemo} from "react";
import {CheckCircle, MapPin, NotepadText, Phone, Star} from "lucide-react";
import {getInitials} from "../lib/utils.ts";
import BusinessHoursDisplay from "./BusinessHoursDisplay.tsx";
import CloseButton from "./CloseButton.tsx";
import {trackContactClick, trackViewProviderClick} from "../lib/algolia-insights.ts";
import type {GeoHit} from "instantsearch.js";
import type {Practice} from "./PracticesMapContainer.tsx";

const PracticeInfoWindow = () => {
    const {state, dispatch} = useApp()

    const practice = useMemo(() => {
        return state.selectedPractice
    }, [state])


    return (
        practice ? <div
            className={"z-10 h-[calc(100vh-25px)] rounded-2xl shadow-2xl w-[360px] bg-white pt-8 px-2 overflow-auto absolute top-1/2 -translate-y-1/2 ml-4 bg-primary text-white"}
            style={{left: state.options.practiceListWidth}}>
            <button onClick={() => dispatch(actions.setSelectedPractice(undefined))}
                    className={"absolute p-1 top-4 right-4 opacity-40 text-green-700 hover:opacity-100 bg-white  cursor-pointer rounded-full"}>
                <CloseButton/>
            </button>
            <div className={"pt-6 text-center"}>
                <img
                    src={`${import.meta.env.PROD ? `https://${import.meta.env.BASE_URL}` : ''}/assets/provider-badge.svg`}
                    alt="" width={90}
                    className={"mx-auto"}/>
                <h2 className={"text-white font-semibold text-xl tracking-wider mt-4"}>
                    {practice.name}
                </h2>

                <p className={"text-white text-xs tracking-wider opacity-80 uppercase font-bold  py-1 rounded-lg mt-1"}>
                    Serving Men & Women
                </p>


            </div>
            <hr className={"border-white opacity-20 my-4"}/>
            <div className={"grid grid-cols-3 gap-1"}>
                <div>
                    <a target={'_blank'}
                       className={"text-center group"}
                       onMouseEnter={() => {
                           trackContactClick(practice as GeoHit<Practice>, 'phone')
                       }}
                       href={`tel:${practice.phone}`}>
                        <span
                            className={"el-icon group-hover:bg-green-700 group-hover:outline-2 group-hover:outline-green-500 bg-green-600 w-12 h-12 flex items-center justify-center rounded-full mx-auto"}><Phone
                            className={"mx-auto"}/></span>
                        <span className={"el-title mt-2 text-sm text-center block"}>Call</span>
                    </a>
                </div>
                <div>
                    <a target={'_blank'}
                       className={"text-center group"}
                       onClick={() => {
                           trackContactClick(practice as GeoHit<Practice>, 'directions')
                       }}
                       href={practice.location.metadata?.google_maps_links?.directionsUri}>
                        <span
                            className={"el-icon group-hover:bg-green-700 group-hover:outline-2 group-hover:outline-green-500 bg-green-600 w-12 h-12 flex items-center justify-center rounded-full mx-auto"}><MapPin
                            className={"mx-auto"}/></span>
                        <span className={"el-title mt-2 text-sm text-center block"}>Directions</span>
                    </a>
                </div>

                <div>
                    <a
                        className={"text-center group"}
                        onClick={() => {
                            trackViewProviderClick(practice as GeoHit<Practice>)
                        }}
                        href={`/hormone-replacement-therapy-provider/${practice.slug}`}>
                        <span
                            className={"el-icon group-hover:bg-green-700 group-hover:outline-2 group-hover:outline-green-500 bg-green-600 w-12 h-12 flex items-center justify-center rounded-full mx-auto"}><NotepadText
                            className={"mx-auto"}/></span>
                        <span className={"el-title mt-2 text-sm text-center block"}>View Profile</span>
                    </a>
                </div>
            </div>
            <div>
                <div>
                    {practice.location?.metadata?.hours && Object.values(practice.location?.metadata?.hours).length > 0 &&
                        <BusinessHoursDisplay regularBusinessHours={practice.location?.metadata?.hours}/>}
                </div>

                {practice.location?.metadata?.reviews && Array.isArray(practice.location?.metadata?.reviews)
                && practice.location?.metadata?.reviews.length > 0
                    ? <div className="flex overflow-x-scroll snap-x snap-mandatory mt-2">
                        {practice.location?.metadata?.reviews.map((review, idx) => {
                            return (
                                <div className="flex-shrink-0 snap-center w-3/4 p-2" key={practice.id + 'review' + idx}>
                                    <div className="bg-green-700 p-3 rounded-lg">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    <div
                                                        className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-semibold text-lg">
                                                        {getInitials(review.authorAttribution.displayName)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-white truncate block w-36">{review.authorAttribution.displayName}</h3>
                                                    <span
                                                        className="text-sm text-white opacity-40">{review.relativePublishTimeDescription}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="flex gap-1">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star
                                                        key={star}
                                                        size={16}
                                                        className="fill-amber-400 text-amber-400"
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-xs text-whitefont-medium">5.0</span>

                                        </div>
                                        <p className="line-clamp-3 text-xs">
                                            {review.text.text}
                                        </p>
                                    </div>
                                </div>)
                        })}

                    </div> : <>
                        <hr className={"border-white opacity-20 my-4"}/>
                        <h2 className={"text-center mb-4 font-bold tracking-wider text-green-500 text-md"}>Why EvexIPEL
                            Approach
                            Matters</h2>

                        <div className={"flex justify-center items-center mt-4 pb-8"}>
                            <ul className={"grid gap-4"}>
                                {
                                    ['Collaborative Care', 'Continuous Education', 'Patient Centered', 'Experienced Team'].map(item => (
                                        <li className={"flex gap-2"} key={item}>
                                            <CheckCircle className={"text-green-500"}/> {item}
                                        </li>
                                    ))

                                }
                            </ul>
                        </div>
                    </>}

            </div>
        </div> : <></>
    )
}

export default PracticeInfoWindow